import { theme } from "antd";
import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// To prevent a close, have a button with the className 'keep-open'. when a close is attempted, the button will be focused
// or provide your own selector
// will match width of target unless otherwise told not to

export function ModalInsert(
  props: PropsWithChildren<{
    targetSelector?: string;
    target?: HTMLElement;
    keepOpenSelector?: string;
    onClose(): void;
    width?: number;
    placement?: "bottomRight";
    offset?: { x?: number; y?: number };
  }>
) {
  const { token } = theme.useToken();
  const [{ x, y, width }, setPos] = useState({
    x: 0,
    y: 0,
    width: props.width,
  });
  if (!props.target && !props.targetSelector)
    throw new Error(
      "ModalInsert requires either a target or a selector for the target"
    );

  useEffect(() => {
    function updatePosition() {
      const target =
        props.target || document.querySelector(props.targetSelector!);
      const box = target?.getBoundingClientRect();
      if (box) {
        const scrollX =
          window.pageXOffset || document.documentElement.scrollLeft;
        const scrollY =
          window.pageYOffset || document.documentElement.scrollTop;

        setPos({
          x:
            box.left +
            scrollX +
            30 -
            (props.placement === "bottomRight"
              ? (props.width || box.width) - box.width
              : 0) +
            (props.offset?.x || 0),
          y: box.top + scrollY + box.height + (props.offset?.y || 0),
          width: props.width || box.width,
        });
      }
    }

    updatePosition();
    window.addEventListener("scroll", updatePosition);
    return () => window.removeEventListener("scroll", updatePosition);
  }, [
    props.target,
    props.targetSelector,
    props.width,
    props.placement,
    props.offset?.y,
    props.offset?.x,
  ]);

  const screen = useRef<HTMLDivElement | null>(null);
  const popup = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const $popup = popup.current;
    const $screen = screen.current;
    let overflow = 0;
    if ($popup && $screen) {
      const inner = $popup.getBoundingClientRect();
      const outer = $screen.getBoundingClientRect();
      overflow = inner.top + inner.height - outer.height;
    }
    if (overflow > 0) setPos((pos) => ({ ...pos, y: pos.y - overflow - 16 }));
  }, [y]);
  return createPortal(
    <div
      ref={screen}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        cursor: "pointer",
        visibility: x === 0 && y === 0 ? "hidden" : "visible",
        zIndex: 104,
      }}
      onClick={(e) => {
        if (screen.current === e.target) {
          const keepOpenButton = screen.current.querySelector(
            props.keepOpenSelector || "button.keep-open"
          );
          if (keepOpenButton) {
            (keepOpenButton as HTMLButtonElement).focus();
          } else props.onClose();
        }
      }}
    >
      <div
        ref={popup}
        style={{
          position: "absolute",
          left: x,
          top: y,
          width: width,
          cursor: "default",
          borderRadius: 14,
          boxShadow: `${token.boxShadowSecondary}, 0 4px 20px 3px rgba(50, 50, 50, 0.25)`,
        }}
      >
        <div
          style={{
            backgroundColor: token.colorBgElevated,
            color: token.colorText,
            padding: 8,
            borderRadius: 14,
            border: `1px solid ${token.colorBorderSecondary}`,
            boxShadow: `${token.boxShadowSecondary}, 0 4px 20px 3px rgba(50, 50, 50, 0.25)`,
          }}
        >
          {props.children}
        </div>
      </div>
    </div>,
    document.querySelector("#screen-insert") || document.body
  );
}
